import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { isArray, assign, isEmpty } from 'lodash';
import { format, parseISO } from 'date-fns';

import { Autocomplete, DateTime, Input } from '~/components/Form';
import { t } from '~/helpers';
import api from '~/services/api';

import * as S from './styles';

export default function MyForm({
  data,
  close,
  sending,
  onForm,
  toggleDisabled,
}) {
  const formRef = useRef(null);

  const { username } = useSelector((state) => state.user.profile);

  const initials = useMemo(
    () =>
      isEmpty(data)
        ? {}
        : assign(data, {
            desc_datahora: format(
              parseISO(data.desc_datahora),
              'dd/MM/yyyy HH:mm:ss'
            ),
            rNewDataEmissaoNF: format(
              parseISO(data.desc_dt_emissao_nf),
              'dd/MM/yyyy HH:mm:ss'
            ),
            rNewNumPedido: data.desc_num_pedido,
            rNewNumNfRvt: data.desc_num_doc,
            rNewNumCTVT: data.desc_veic_doc,
            rAtualVolNF: data.desc_vol_temp_car_doc,
            rAtualVol20NF: data.desc_vol_20_doc,
            rAtualTemperaturaNF: data.desc_temp_car_doc,
            rAtualDensidadeNF: data.desc_dens_20_doc,
          }),
    [data]
  );

  const handleSubmit = useCallback(
    async (value) => {
      try {
        await toggleDisabled(true);

        const dataForm = assign(value, {});
        const regex = /^[a-z A-Z 0-9 ãõáéíóúñÁÉÍÓÚÑ \(\)\[\]\/\:\?\!\@\$\%\,\-\.\+";]+$/;

        const schema = Yup.object().shape({
          rNewNumPedido: Yup.string()
            .typeError('Campo Obrigatório.')
            .required('Campo obrigatório.')
            .max(10, 'Informe no máximo 10 caracteres.'),
          rNewNumNfRvt: Yup.string()
            .typeError('Campo Obrigatório.')
            .required('Campo obrigatório.')
            .max(10, 'Informe no máximo 10 caracteres.'),
          rNewNumCTVT: Yup.string()
            .typeError('Campo Obrigatório.')
            .required('Campo obrigatório.')
            .max(10, 'Informe no máximo 10 caracteres.'),
          rNewDataEmissaoNF: Yup.string().required('Campo obrigatório.'),
          rMaag_Usua_Sol: Yup.string().required('Campo obrigatório.'),
          rMaag_Obs: Yup.string()
            .required('Campo obrigatório.')
            .max(500, 'São permitidos apenas 500 caracteres.')
            .matches(regex, 'Caracteres inválidos!'),
        });

        await schema.validate(value, { abortEarly: false });

        delete value.post_desc;
        delete value.desc_datahora;
        delete value.desc_vol_20_total;

        const formData = assign(
          {
            rCodigo: data.desc_cod_desc,
            rMaag_Tabela: 'DESCARGA',
            rMata_Cod_Mata: 4,
            rMaag_Num_Tar_Exec: 3,
            rMaag_Chave: `(POST_COD_POSTO = &#183${data.post_cod_posto}&#183 AND ABAS_COD_ABAS = ${data.desc_cod_desc})`,
            rPost_Cod_Posto: data.post_cod_posto,
            rBico_cod_bico: '',
            rTurn_cod_turn: data.turn_cod_turn,
            rMaag_Usua_Inc: username,
            rNewEmpe_Cod_Emp: '',
            rNewVeic_Cod_Veic: '',
            rNewMoto_cod_moto: '',
            rNewEnvia_Unilog: 'N',
            rAtualAbas_Vol_Amb: '0.0',
            rAtualAbas_Vol_Ref: '0.0',
            rAtualAbas_Vol_20: '0.0',
            rNF_AlterarVolumes: 'N',
            rOldVolNF: data.desc_vol_temp_car_doc,
            rOldVol20NF: data.desc_vol_20_doc,
          },
          value
        );

        await api.post('manutencaocontrole', formData);

        toast.success('Manutenção agendada com sucesso !');

        await onForm(assign(dataForm, { desc_num_doc: value.rNewNumNfRvt }));
        await close();
      } catch (err) {
        const validationErrors = {};

        if (err instanceof Yup.ValidationError) {
          err.inner.forEach((error) => {
            validationErrors[error.path] = error.message.replace(
              `${error.path} `,
              ''
            );
          });

          formRef.current.setErrors(validationErrors);
        } else {
          if (isArray(err)) err.map((e) => toast.error(t(e)));
          else toast.error(t(err));
        }
      } finally {
        await toggleDisabled(false);
      }
    },
    [data, username]
  );

  useEffect(() => {
    if (sending) formRef.current.submitForm();
  }, [sending]);

  if (!isEmpty(data))
    return (
      <S.WrapperForm
        ref={formRef}
        onSubmit={handleSubmit}
        initialData={initials}
      >
        <Input disabled name="post_desc" label="Posto" />

        <DateTime disabled time name="desc_datahora" label="Data" />

        <Input disabled name="desc_vol_20_total" label="Volume" />

        <Input name="rNewNumPedido" label="Número Pedido" />

        <Input name="rNewNumNfRvt" label="Número NF/RVT" />

        <Input name="rNewNumCTVT" label="Número VT/CT" />

        <DateTime name="rNewDataEmissaoNF" label="Data Emissão NF" />

        <Input disabled name="rAtualVolNF" label="Volume NF" />

        <Input disabled name="rAtualVol20NF" label="Volume 20 NF" />

        <Input disabled name="rAtualTemperaturaNF" label="Temperatura NF" />

        <Input disabled name="rAtualDensidadeNF" label="Densidade NF" />

        <Autocomplete
          name="rMaag_Usua_Sol"
          label="usua_nome"
          unique="usua_cod_usua"
          url="usuario"
          title="Usuário Solicitante"
        />

        <Input multiline name="rMaag_Obs" label="MSG" rows={4} />
      </S.WrapperForm>
    );

  return <div />;
}

MyForm.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  close: PropTypes.func.isRequired,
  sending: PropTypes.bool.isRequired,
  onForm: PropTypes.func.isRequired,
  toggleDisabled: PropTypes.func.isRequired,
};
